var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { ENTER_KEY, ESCAPE_KEY, F_CODE } from '#constants';
import { C } from '#integrations';
import { debounceRAF } from '#services';
import { Box } from '../Box';
import { IconButton } from '../buttons';
import { SearchSmIcon, XCloseIcon } from '../icons';
import { InputView } from '../inputs/components/InputView';
import { SearchTestIdFactory } from './interactions';
import styles from './Search.module.css';
const EMPTY_STRING = '';
const TEXT_INPUT_DEBOUNCE_TIMEOUT = 300;
export const Search = ({ ariaControls, ariaLabel, className, closeLabel, 'data-testid': testId, initialText, inputClassName, isActive: isActiveFromParent, kind = 'stand-alone', magnifierLabel, onChange, placeholder, }) => {
    _react_refresh_temp_2();
    // The embedded Search component is always visible and active
    const [isActive, setIsActive] = useState(isActiveFromParent || kind === 'embedded' ? true : undefined);
    const [text, setText] = useState(initialText || EMPTY_STRING);
    const inputRef = useRef(null);
    const magnifierButtonRef = useRef(null);
    const closeButtonRef = useRef(null);
    const isStandAlone = kind === 'stand-alone' || kind === 'input';
    useEffect(() => {
        if (isActiveFromParent !== undefined) {
            setIsActive(isActiveFromParent);
        }
    }, [isActiveFromParent]);
    const onChangeRef = useRef(() => onChange(text));
    useEffect(() => {
        onChangeRef.current = () => onChange(text);
    }, [text, onChange]);
    const debouncedCallback = useMemo(() => {
        const onChangeHandler = () => {
            if (onChangeRef.current) {
                onChangeRef.current();
            }
        };
        return debounceRAF(onChangeHandler, TEXT_INPUT_DEBOUNCE_TIMEOUT);
    }, []);
    useEffect(() => {
        if (isActive && inputRef.current) {
            inputRef.current.focus();
        }
        if (isActive === false) {
            setText(EMPTY_STRING);
            onChange(EMPTY_STRING);
            if (magnifierButtonRef.current) {
                magnifierButtonRef.current.focus();
            }
        }
    }, [isActive, onChange]);
    const onInputChange = useCallback((e) => {
        const newValue = e.target.value;
        setText(newValue);
        debouncedCallback();
    }, [debouncedCallback]);
    // The input kind has no collapsed state, so Escape only clears its text
    const isEscapeHandled = kind === 'input' ? text !== EMPTY_STRING : kind === 'stand-alone';
    const onInputKeyUp = (ev) => {
        if (ev.key !== ESCAPE_KEY || !isEscapeHandled) {
            return;
        }
        if (kind === 'input') {
            setText(EMPTY_STRING);
            onChange(EMPTY_STRING);
        }
        else {
            setIsActive(false);
        }
    };
    const onInputKeyDown = (ev) => {
        // An unhandled Escape stays free for an open Drawer or Modal to close on
        if (ev.key === ESCAPE_KEY && isEscapeHandled) {
            ev.preventDefault();
        }
        if (ev.key === ENTER_KEY && closeButtonRef.current) {
            ev.preventDefault();
            closeButtonRef.current.focus();
        }
    };
    useEffect(() => {
        const onGlobalKeyDown = (ev) => {
            if (ev.code === F_CODE && (ev.ctrlKey || ev.metaKey)) {
                ev.preventDefault();
                ev.stopPropagation();
                setIsActive(true);
                // isActive may already be true, which skips the focus effect
                inputRef.current?.focus();
            }
        };
        if (isStandAlone) {
            window.addEventListener('keydown', onGlobalKeyDown, false);
        }
        return () => {
            window.removeEventListener('keydown', onGlobalKeyDown, false);
        };
    }, [isStandAlone]);
    if (kind === 'input') {
        return (_jsx(InputView, { "aria-controls": ariaControls, "aria-label": ariaLabel, "data-testid": testId, inputClassName: inputClassName, onChange: onInputChange, onKeyDown: onInputKeyDown, onKeyUp: onInputKeyUp, placeholder: placeholder, ref: inputRef, StartIcon: SearchSmIcon, testComponentPrefix: SearchTestIdFactory.getSearchBase(), type: "text", value: text }));
    }
    return (_jsxs(Box, { ...(isActive && { gap: 's' }), shrink: 0, children: [kind === 'embedded' && (_jsx(SearchSmIcon, { className: styles.InlineIcon, size: "small" })), _jsx("input", { "aria-label": ariaLabel, className: C(className && className, styles.Input, kind === 'stand-alone' &&
                    (isActive
                        ? C(styles.InputVisible, inputClassName)
                        : styles.InputHidden)), "data-testid": SearchTestIdFactory.getSearchInputTestId(testId), onChange: onInputChange, onKeyDown: onInputKeyDown, onKeyUp: onInputKeyUp, placeholder: placeholder, ref: inputRef, type: "text", value: text, ...(isStandAlone &&
                    !isActive && { tabIndex: -1, 'aria-hidden': true }) }), isStandAlone &&
                (isActive ? (_jsx(IconButton, { "aria-label": closeLabel || '', "data-testid": SearchTestIdFactory.getSearchCloseButtonTestId(testId), Icon: XCloseIcon, kind: "ghost", onClick: () => setIsActive(false), ref: closeButtonRef })) : (_jsx(IconButton, { "aria-label": magnifierLabel || '', "data-testid": SearchTestIdFactory.getSearchMagnifierButtonTestId(testId), Icon: SearchSmIcon, kind: "ghost", onClick: () => setIsActive(true), ref: magnifierButtonRef })))] }));
};
_react_refresh_temp_1 = Search;
$RefreshReg$(_react_refresh_temp_1, "Search");
_react_refresh_temp_2(Search, "H3Nam44j5QCLAVYBDa1Y80kp95E=");
